import {
  BookingItemRequest,
  CheckoutStatus,
  Money,
  OPEN_CHECKOUT_STATUSES,
  SelectedSession,
} from '../../types/booking/booking'

export const sessionKey = (session: { courtId: number; startTime: string }) =>
  `${session.courtId}|${session.startTime}`

/** Half-open intervals [start, end): back-to-back sessions do not overlap. */
export const overlaps = (
  a: { startTime: string; endTime: string },
  b: { startTime: string; endTime: string }
) => a.startTime < b.endTime && a.endTime > b.startTime

export const toBookingItems = (sessions: SelectedSession[]): BookingItemRequest[] =>
  sessions.map(session => ({
    courtId: session.courtId,
    startTime: session.startTime,
    durationMinutes: session.durationMinutes,
  }))

export const sortSessions = (sessions: SelectedSession[]) =>
  [...sessions].sort((a, b) =>
    a.startTime === b.startTime ? a.courtId - b.courtId : a.startTime.localeCompare(b.startTime)
  )

export const formatMoney = (money?: Money | number | null, currency = 'ron') => {
  if (money === undefined || money === null) {
    return ''
  }
  const amount = typeof money === 'number' ? money : money.amount
  const code = (typeof money === 'number' ? currency : money.currency || currency).toUpperCase()
  const rounded = Number.isInteger(amount) ? String(amount) : amount.toFixed(2)
  return `${rounded} ${code}`
}

/** Refund points as a plain number (1 point = 1 RON): whole points without decimals. */
export const formatPoints = (points?: number | null) => {
  const value = points ?? 0
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

export const formatMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) {
    return `${rest} min`
  }
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}min`
}

export const isOpenCheckout = (status?: CheckoutStatus) =>
  status !== undefined && OPEN_CHECKOUT_STATUSES.includes(status)

export const newRequestId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = (Math.random() * 16) | 0
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16)
  })
}

const ATTEMPT_PREFIX = 'bounce.checkout-attempt.'
/** Longer than the hold, so a late retry of the same attempt still finds its checkout instead of holding again. */
const ATTEMPT_TTL_MS = 30 * 60 * 1000

type StoredAttempt = { requestId: string; createdAt: number }

const readStorage = (key: string): StoredAttempt | null => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as StoredAttempt) : null
  } catch {
    return null
  }
}

/**
 * Client request id of one checkout attempt. The same selection (same user, sessions, funding and recipient) reuses the
 * id for a while, so a retry, a double click or the same attempt in another tab returns the existing hold instead of
 * creating a second one. A different selection is a new attempt.
 */
export const attemptRequestId = (signature: string): string => {
  const key = ATTEMPT_PREFIX + signature
  const stored = readStorage(key)
  if (stored && Date.now() - stored.createdAt < ATTEMPT_TTL_MS) {
    return stored.requestId
  }
  const attempt: StoredAttempt = { requestId: newRequestId(), createdAt: Date.now() }
  try {
    window.localStorage.setItem(key, JSON.stringify(attempt))
  } catch {
    // Storage unavailable (private mode): the id still protects against double submits within this page.
  }
  return attempt.requestId
}

/** Forgets an attempt once it reached a final outcome, so booking the same sessions again starts a new checkout. */
export const forgetAttempt = (signature: string) => {
  try {
    window.localStorage.removeItem(ATTEMPT_PREFIX + signature)
  } catch {
    // ignore
  }
}

export const attemptSignature = (parts: unknown[]) => JSON.stringify(parts)

/** Wall-clock HH:mm of a local date-time string. */
export const timeOf = (localDateTime: string) => localDateTime.slice(11, 16)

export const CHECKOUT_PATH = '/checkout'

export const checkoutPath = (checkoutId: string) =>
  `${CHECKOUT_PATH}?checkoutId=${encodeURIComponent(checkoutId)}`
